import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Plus, Save, Trash2, Edit2, GripVertical, ArrowLeft, Loader2, RefreshCw,
  Sparkles, Star, StarOff, Eye, EyeOff, ExternalLink, Tag, Film, AlertCircle, Play,
} from 'lucide-react';
import AiGenerateButton from '@/components/ai/AiGenerateButton';

const detectPlatform = (url) => {
  const u = (url || '').toLowerCase();
  if (u.includes('tiktok.com')) return 'tiktok';
  if (u.includes('facebook.com') || u.includes('fb.com') || u.includes('fb.watch')) return 'facebook';
  if (u.includes('instagram.com')) return 'instagram';
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'youtube';
  return 'other';
};

const PLATFORM_BADGE = {
  tiktok:    { label: 'TikTok',    color: 'bg-black text-white' },
  facebook:  { label: 'Facebook',  color: 'bg-[#1877F2] text-white' },
  instagram: { label: 'Instagram', color: 'bg-gradient-to-br from-[#FEDA75] to-[#D62976] text-white' },
  youtube:   { label: 'YouTube',   color: 'bg-[#FF0000] text-white' },
  other:     { label: 'Video',     color: 'bg-surface-2 text-ink' },
};

export default function VideoManager() {
  const [view, setView] = useState('list'); // 'list' | 'categories'
  const [videos, setVideos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const dragIdRef = useRef(null);
  const [dragOverId, setDragOverId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [v, c] = await Promise.all([
        api.get('/admin/videos').then((r) => r.data?.items || []),
        api.get('/admin/video-categories').then((r) => r.data?.items || []),
      ]);
      setVideos(v); setCategories(c);
    } catch (e) {
      toast.error('Could not load videos', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreate = () => {
    setEditing({
      id: null, url: '', platform: '', title: '', description: '',
      category: '', thumbnail: '', featured: false, published: true, embed_mode: '',
    });
    setCreating(true);
  };

  const onSave = async (form) => {
    try {
      if (creating || !form.id) {
        await api.post('/admin/videos', form);
        toast.success('Video added.', { description: 'Embed metadata fetched.' });
      } else {
        await api.put(`/admin/videos/${form.id}`, form);
        toast.success('Video saved.');
      }
      setEditing(null); setCreating(false);
      load();
    } catch (e) {
      toast.error('Save failed', { description: String(e?.response?.data?.detail || e.message).slice(0, 220) });
    }
  };

  const onDelete = async (v) => {
    if (!window.confirm(`Delete this video?\n\n${v.title || v.url}`)) return;
    try {
      await api.delete(`/admin/videos/${v.id}`);
      toast.success('Video deleted.');
      load();
    } catch (e) {
      toast.error('Delete failed', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    }
  };

  const onToggleField = async (v, field) => {
    try {
      await api.put(`/admin/videos/${v.id}`, { [field]: !v[field] });
      load();
    } catch (e) {
      toast.error('Update failed', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    }
  };

  const onRefreshEmbed = async (v) => {
    try {
      await api.post(`/admin/videos/${v.id}/refresh-embed`);
      toast.success('Embed refreshed.');
      load();
    } catch (e) {
      toast.error('Refresh failed', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    }
  };

  // Drag & drop reorder
  const onDragStart = (e, id) => { dragIdRef.current = id; e.dataTransfer.effectAllowed = 'move'; };
  const onDragOver = (e, id) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverId(id); };
  const onDragLeave = () => setDragOverId(null);
  const onDrop = async (e, targetId) => {
    e.preventDefault();
    const sourceId = dragIdRef.current; dragIdRef.current = null; setDragOverId(null);
    if (!sourceId || sourceId === targetId) return;
    const sIdx = videos.findIndex((i) => i.id === sourceId);
    const tIdx = videos.findIndex((i) => i.id === targetId);
    if (sIdx < 0 || tIdx < 0) return;
    const reordered = [...videos];
    const [moved] = reordered.splice(sIdx, 1);
    reordered.splice(tIdx, 0, moved);
    setVideos(reordered);
    try {
      await api.put('/admin/videos/reorder', { ids: reordered.map((i) => i.id) });
      toast.success('Order updated.');
    } catch (e2) {
      toast.error('Reorder failed', { description: String(e2?.response?.data?.detail || e2.message).slice(0, 200) });
      load();
    }
  };

  if (editing) {
    return (
      <VideoEditor
        video={editing}
        isNew={creating}
        categories={categories}
        onCancel={() => { setEditing(null); setCreating(false); }}
        onSave={onSave}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="video-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-gold font-semibold">Real Talk</div>
          <h2 className="font-display uppercase text-3xl mt-1">Videos</h2>
          <p className="text-sm text-muted-kc mt-1">Paste a TikTok or Facebook URL → it embeds + appears on <code className="font-mono text-xs">/videos</code> automatically.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onCreate} className="h-10 px-4 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="video-create">
            <Plus size={16}/> New video
          </button>
        </div>
      </div>

      <div className="flex gap-1 bg-surface-2 p-1 rounded-full w-fit border border-kc">
        <SubTab active={view === 'list'} onClick={() => setView('list')} icon={Film} label="Videos" count={videos.length} testid="vm-tab-videos"/>
        <SubTab active={view === 'categories'} onClick={() => setView('categories')} icon={Tag} label="Categories" count={categories.length} testid="vm-tab-categories"/>
      </div>

      {view === 'categories' ? (
        <CategoryManager categories={categories} reload={load}/>
      ) : loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin text-muted-kc" size={24}/></div>
      ) : videos.length === 0 ? (
        <EmptyState onCreate={onCreate}/>
      ) : (
        <div className="space-y-2" data-testid="videos-rows">
          {videos.map((v) => (
            <VideoRow
              key={v.id}
              video={v}
              categories={categories}
              dragOver={dragOverId === v.id}
              onDragStart={(e) => onDragStart(e, v.id)}
              onDragOver={(e) => onDragOver(e, v.id)}
              onDragLeave={onDragLeave}
              onDrop={(e) => onDrop(e, v.id)}
              onEdit={() => setEditing(v)}
              onDelete={() => onDelete(v)}
              onToggleField={(f) => onToggleField(v, f)}
              onRefreshEmbed={() => onRefreshEmbed(v)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const SubTab = ({ active, onClick, icon: Icon, label, count, testid }) => (
  <button type="button" onClick={onClick} className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 transition-colors ${active ? 'bg-ink text-white' : 'text-muted-kc hover:bg-white'}`} data-testid={testid}>
    <Icon size={13}/> {label} <span className="opacity-60">({count})</span>
  </button>
);

const EmptyState = ({ onCreate }) => (
  <div className="text-center py-16 border-2 border-dashed border-kc-strong rounded-[var(--kc-radius-md)] bg-surface-2">
    <Film size={36} className="mx-auto text-muted-kc mb-3"/>
    <div className="font-display uppercase text-xl mb-1">No videos yet</div>
    <div className="text-sm text-muted-kc mb-4">Grab a TikTok or Facebook share URL and paste it here.</div>
    <button onClick={onCreate} className="h-10 px-4 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors">
      <Plus size={16}/> Add your first video
    </button>
  </div>
);

function VideoRow({ video, categories, dragOver, onDragStart, onDragOver, onDragLeave, onDrop, onEdit, onDelete, onToggleField, onRefreshEmbed }) {
  const badge = PLATFORM_BADGE[video.platform] || PLATFORM_BADGE.other;
  const cat = video.category && categories.find((c) => c.slug === video.category);
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`bg-white border rounded-[var(--kc-radius-md)] p-3 flex items-center gap-3 transition-colors ${dragOver ? 'border-[color:var(--kc-gold)] bg-gold-soft' : 'border-kc hover:border-kc-strong'} ${!video.published ? 'opacity-60' : ''}`}
      data-testid={`video-row-${video.id}`}
    >
      <button type="button" className="cursor-grab active:cursor-grabbing text-muted-kc hover:text-ink p-1" title="Drag to reorder" aria-label="Drag to reorder">
        <GripVertical size={16}/>
      </button>
      <div className="w-20 h-20 rounded-[var(--kc-radius-sm)] bg-surface-2 border border-kc overflow-hidden flex-shrink-0 relative">
        {video.thumbnail ? (
          <img src={video.thumbnail} alt="" className="w-full h-full object-cover"/>
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Film size={20} className="text-muted-kc"/></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${badge.color}`}>{badge.label}</span>
          {cat && <span className="inline-block text-[10px] uppercase tracking-wider font-bold bg-gold-soft text-[color:var(--kc-gold-2)] border border-[color:var(--kc-gold)]/40 px-2 py-0.5 rounded-full">{cat.name}</span>}
          {video.featured && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-[color:var(--kc-gold)] text-black px-2 py-0.5 rounded-full"><Star size={10} fill="currentColor"/> Featured</span>}
        </div>
        <div className="font-semibold text-sm truncate">{video.title || <span className="italic text-muted-kc">Untitled</span>}</div>
        <a href={video.url} target="_blank" rel="noreferrer" className="text-xs text-muted-kc font-mono truncate inline-block max-w-full hover:underline">{video.url}</a>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button onClick={() => onToggleField('featured')} className={`h-8 px-2 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold transition-colors ${video.featured ? 'text-[color:var(--kc-gold-2)] hover:bg-gold-soft' : 'text-muted-kc hover:bg-surface-2 hover:text-ink'}`} title={video.featured ? 'Unfeature' : 'Feature'} data-testid={`video-feature-${video.id}`}>
          {video.featured ? <Star size={14} fill="currentColor"/> : <StarOff size={14}/>}
        </button>
        <button onClick={() => onToggleField('published')} className={`h-8 px-2 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold transition-colors ${video.published ? 'text-[color:var(--kc-success)] hover:bg-green-50' : 'text-muted-kc hover:bg-surface-2'}`} title={video.published ? 'Unpublish' : 'Publish'} data-testid={`video-publish-${video.id}`}>
          {video.published ? <Eye size={14}/> : <EyeOff size={14}/>}
        </button>
        <button onClick={onRefreshEmbed} className="h-8 px-2 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold text-muted-kc hover:bg-surface-2 hover:text-ink transition-colors" title="Refresh embed metadata" data-testid={`video-refresh-${video.id}`}>
          <RefreshCw size={13}/>
        </button>
        <button onClick={onEdit} className="h-8 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold bg-ink text-white hover:bg-black/90 transition-colors" data-testid={`video-edit-${video.id}`}>
          <Edit2 size={12}/> Edit
        </button>
        <button onClick={onDelete} className="h-8 px-2 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold text-[color:var(--kc-danger)] hover:bg-red-50 transition-colors" data-testid={`video-delete-${video.id}`}>
          <Trash2 size={12}/>
        </button>
      </div>
    </div>
  );
}

function VideoEditor({ video, isNew, categories, onCancel, onSave }) {
  const [form, setForm] = useState(video);
  const [saving, setSaving] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const thumbInputRef = useRef(null);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const detectedPlatform = useMemo(() => detectPlatform(form.url), [form.url]);
  const showPlatformWarn = form.url && detectedPlatform === 'other';

  // Smart default for embed_mode based on platform
  const effectiveMode = form.embed_mode || (detectedPlatform === 'tiktok' || detectedPlatform === 'youtube' ? 'embed' : 'link_out');

  const errors = useMemo(() => {
    const e = {};
    if (!form.url.trim()) e.url = 'URL is required.';
    else if (!form.url.startsWith('http://') && !form.url.startsWith('https://')) e.url = 'Must start with http:// or https://';
    return e;
  }, [form]);
  const canSave = Object.keys(errors).length === 0;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    await onSave({ ...form, platform: detectedPlatform, embed_mode: effectiveMode });
    setSaving(false);
  };

  const uploadThumb = async (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error('Image too large', { description: 'Max 12 MB.' });
      return;
    }
    setUploadingThumb(true);
    const fd = new FormData(); fd.append('file', file); fd.append('purpose', 'video-thumb');
    try {
      const r = await api.post('/admin/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setField('thumbnail', r.data.url);
      toast.success('Thumbnail uploaded.');
    } catch (e) {
      toast.error('Upload failed', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    } finally {
      setUploadingThumb(false);
      if (thumbInputRef.current) thumbInputRef.current.value = '';
    }
  };

  const thumbSrc = form.thumbnail && !form.thumbnail.startsWith('http') && !form.thumbnail.startsWith('/splash')
    ? `${process.env.REACT_APP_BACKEND_URL || ''}${form.thumbnail}`
    : form.thumbnail;

  return (
    <div className="space-y-4 max-w-3xl" data-testid="video-editor">
      <div className="flex items-center justify-between gap-3 bg-white border border-kc rounded-[var(--kc-radius-md)] p-3 sticky top-2 z-10 shadow-sm">
        <button onClick={onCancel} className="h-9 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm font-semibold text-muted-kc hover:bg-surface-2 transition-colors" data-testid="video-back">
          <ArrowLeft size={14}/> Back
        </button>
        <button onClick={handleSave} disabled={!canSave || saving} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-40 transition-colors" data-testid="video-save">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} {isNew ? 'Add video' : 'Save'}
        </button>
      </div>

      <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4 space-y-4">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Video URL <span className="text-[color:var(--kc-danger)]">*</span></label>
          <input
            value={form.url}
            onChange={(e) => setField('url', e.target.value)}
            placeholder="https://www.tiktok.com/@you/video/...   or   https://www.facebook.com/..."
            className="kc-tinput font-mono text-sm"
            data-testid="video-url"
          />
          {errors.url && <Hint type="error">{errors.url}</Hint>}
          {form.url && (
            <div className="mt-2 text-xs flex items-center gap-2 flex-wrap">
              <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${(PLATFORM_BADGE[detectedPlatform] || PLATFORM_BADGE.other).color}`}>
                Detected: {(PLATFORM_BADGE[detectedPlatform] || PLATFORM_BADGE.other).label}
              </span>
              <a href={form.url} target="_blank" rel="noreferrer" className="text-muted-kc inline-flex items-center gap-1 hover:underline">
                Open <ExternalLink size={11}/>
              </a>
            </div>
          )}
          {showPlatformWarn && (
            <Hint type="info">Heads up — we couldn’t auto-detect the platform. TikTok and YouTube embed best inline. Facebook & Instagram are most reliable in “Open in new tab” mode.</Hint>
          )}
          {isNew && <Hint>When you save, we’ll fetch the thumbnail + embed metadata automatically.</Hint>}
        </div>

        {/* PLAYBACK MODE */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider mb-2">Playback mode</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { id: 'embed',    title: 'Inline embed',       hint: 'Plays in a modal on the site. Best for TikTok / YouTube.' },
              { id: 'link_out', title: 'Open in new tab',    hint: 'Opens the video on its native platform. Most reliable for Facebook & Instagram.' },
            ].map((opt) => {
              const active = effectiveMode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setField('embed_mode', opt.id)}
                  className={`p-3 rounded-[var(--kc-radius-md)] border text-left transition-colors ${active ? 'border-[color:var(--kc-gold)] bg-gold-soft' : 'border-kc hover:border-kc-strong bg-white'}`}
                  data-testid={`video-mode-${opt.id}`}
                >
                  <div className="text-sm font-semibold inline-flex items-center gap-1.5">
                    {opt.id === 'embed' ? <Play size={13} fill="currentColor"/> : <ExternalLink size={13}/>}
                    {opt.title}
                  </div>
                  <div className="text-[11px] text-muted-kc mt-0.5">{opt.hint}</div>
                </button>
              );
            })}
          </div>
          {detectedPlatform === 'facebook' && (
            <Hint type="info">Facebook’s inline player often shows “Video Unavailable” for non-Page videos and on iOS. Keeping this set to <strong>Open in new tab</strong> is the most reliable choice for FB.</Hint>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between gap-2 mb-1">
              <label className="block text-xs font-semibold uppercase tracking-wider">Title (shows on card + modal)</label>
              <AiGenerateButton page="generic" field="video_title" currentValue={form.title || ''} onApply={(t) => setField('title', t)} length="short" extraContext={{ description: form.description, platform: detectedPlatform }}/>
            </div>
            <input value={form.title} onChange={(e) => setField('title', e.target.value)} placeholder="Optional — pulled from the post if blank" className="kc-tinput" data-testid="video-title"/>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Category</label>
            <select value={form.category || ''} onChange={(e) => setField('category', e.target.value)} className="kc-tinput" data-testid="video-category">
              <option value="">— No category —</option>
              {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
            </select>
            {categories.length === 0 && <Hint>Create categories in the Categories sub-tab to organize videos.</Hint>}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between gap-2 mb-1">
            <label className="block text-xs font-semibold uppercase tracking-wider">Description (optional — shows in the modal)</label>
            <AiGenerateButton page="generic" field="video_description" currentValue={form.description || ''} onApply={(t) => setField('description', t)} length="medium" extraContext={{ title: form.title, platform: detectedPlatform }}/>
          </div>
          <textarea value={form.description} onChange={(e) => setField('description', e.target.value)} rows={3} className="kc-tinput" placeholder="A short context line to add to the modal — what the video covers, why it matters." data-testid="video-description"/>
        </div>

        {/* THUMBNAIL — uploader + URL */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Thumbnail {detectedPlatform === 'facebook' && <span className="text-[color:var(--kc-gold-2)] normal-case font-normal tracking-normal">(strongly recommended for Facebook — no auto-fetch)</span>}</label>
          {form.thumbnail ? (
            <div className="p-3 border border-[color:var(--kc-gold)] bg-gold-soft rounded-[var(--kc-radius-md)] flex items-start gap-3">
              <img src={thumbSrc} alt="thumb" className="w-20 h-20 object-cover rounded-[var(--kc-radius-sm)] border border-kc flex-shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-muted-kc break-all">{form.thumbnail}</div>
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs font-semibold">
                  <button type="button" onClick={() => thumbInputRef.current?.click()} className="underline hover:text-[color:var(--kc-gold-2)]">Replace upload</button>
                  <span className="text-muted-kc/60">·</span>
                  <button type="button" onClick={() => setField('thumbnail', '')} className="text-[color:var(--kc-danger)] underline">Remove</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => !uploadingThumb && thumbInputRef.current?.click()}
                className="w-full p-4 border-2 border-dashed border-kc-strong rounded-[var(--kc-radius-md)] text-center hover:bg-surface-2 transition-colors text-sm"
                data-testid="video-thumb-upload"
              >
                {uploadingThumb ? <span className="inline-flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Uploading…</span> : <span>📤 Click to upload custom thumbnail · PNG / JPG / WebP · max 12 MB</span>}
              </button>
              <div className="text-[11px] text-muted-kc text-center">or paste a thumbnail URL:</div>
              <input value={form.thumbnail || ''} onChange={(e) => setField('thumbnail', e.target.value)} placeholder="https://..." className="kc-tinput" data-testid="video-thumbnail"/>
            </div>
          )}
          <input ref={thumbInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadThumb(e.target.files?.[0])}/>
          {detectedPlatform === 'tiktok' && !form.thumbnail && <Hint>TikTok will auto-fetch a thumbnail when you save. You can override here.</Hint>}
        </div>

        <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-kc">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!form.featured} onChange={(e) => setField('featured', e.target.checked)} className="kc-checkbox" data-testid="video-featured"/>
            <span className="text-sm font-semibold inline-flex items-center gap-1"><Star size={13} className="text-[color:var(--kc-gold-2)]"/> Featured (pin to hero)</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!form.published} onChange={(e) => setField('published', e.target.checked)} className="kc-checkbox" data-testid="video-published"/>
            <span className="text-sm font-semibold">Published</span>
          </label>
        </div>
      </div>
    </div>
  );
}

/* ===== Category Manager ===== */
function CategoryManager({ categories, reload }) {
  const [name, setName] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post('/admin/video-categories', { name: name.trim() });
      setName('');
      toast.success('Category added.');
      reload();
    } catch (e) {
      toast.error('Could not add', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?\n\nVideos using it will lose their category but remain published.`)) return;
    try {
      await api.delete(`/admin/video-categories/${c.id}`);
      toast.success('Deleted.');
      reload();
    } catch (e) {
      toast.error('Delete failed', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    }
  };

  const rename = async (c) => {
    const newName = (editing?.name || '').trim();
    if (!newName || newName === c.name) { setEditing(null); return; }
    try {
      await api.put(`/admin/video-categories/${c.id}`, { name: newName });
      setEditing(null);
      reload();
    } catch (e) {
      toast.error('Rename failed', { description: String(e?.response?.data?.detail || e.message).slice(0, 200) });
    }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="category-manager">
      <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4 space-y-3">
        <label className="block text-xs font-semibold uppercase tracking-wider">New category</label>
        <div className="flex gap-2">
          <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} placeholder="e.g. Mindset, Q&A, Behind the scenes" className="kc-tinput flex-1" data-testid="category-name-input"/>
          <button onClick={create} disabled={saving || !name.trim()} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-40 transition-colors" data-testid="category-add">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Plus size={14}/>} Add
          </button>
        </div>
      </div>

      {categories.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-kc-strong rounded-[var(--kc-radius-md)] bg-surface-2">
          <Tag size={28} className="mx-auto text-muted-kc mb-2"/>
          <div className="text-sm text-muted-kc">No categories yet. Add one above.</div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="categories-list">
          {categories.map((c) => (
            <div key={c.id} className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-3 flex items-center gap-3" data-testid={`category-row-${c.slug}`}>
              <Tag size={16} className="text-[color:var(--kc-gold-2)] flex-shrink-0"/>
              <div className="flex-1 min-w-0">
                {editing?.id === c.id ? (
                  <input
                    autoFocus
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    onBlur={() => rename(c)}
                    onKeyDown={(e) => { if (e.key === 'Enter') rename(c); if (e.key === 'Escape') setEditing(null); }}
                    className="kc-tinput w-full"
                  />
                ) : (
                  <div className="font-semibold text-sm">{c.name} <span className="text-muted-kc font-mono text-xs">/ {c.slug}</span></div>
                )}
              </div>
              <button onClick={() => setEditing({ id: c.id, name: c.name })} className="h-8 px-2 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold text-muted-kc hover:bg-surface-2 hover:text-ink transition-colors">
                <Edit2 size={12}/>
              </button>
              <button onClick={() => remove(c)} className="h-8 px-2 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-xs font-semibold text-[color:var(--kc-danger)] hover:bg-red-50 transition-colors">
                <Trash2 size={12}/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Hint = ({ children, type = 'info' }) => {
  const cls = type === 'error' ? 'text-[color:var(--kc-danger)]' : 'text-muted-kc';
  return <div className={`text-[11px] mt-1 inline-flex items-center gap-1 ${cls}`}><AlertCircle size={11}/> {children}</div>;
};
